import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type { Archetype, GenerationJob, Page, TagResponse, Topic } from "@/types";

/**
 * React Query hooks for the admin Topics feature — the final, largest slice of
 * the admin migration. These wrap the existing API client methods (the client
 * is unchanged) and own the query keys, caching, invalidation-on-success, and
 * generation-job polling (via `refetchInterval`) that previously lived as four
 * manual `useEffect` data-loaders + manual refetch in the page.
 *
 * The topics list key incorporates the FULL filter set + page + sort, so any
 * filter/search/sort/page change changes the query key and React Query refetches
 * naturally — this is what lets the page drop its "reload on filter change"
 * loader suppressions. The generation-jobs list is keyed by its own status +
 * page and polls while any job is still running. Follows the Sources / Seed
 * Events / Assignments slice templates.
 */

export interface TopicsListParams {
  page: number;
  pageSize: number;
  topicAreaId?: string;
  status?: "draft" | "ready" | "assigned";
  q?: string;
  sort_by?: "title" | "created_at";
  sort_order?: "asc" | "desc";
  includeDrafts?: boolean;
  topicArea?: string;
  angleType?: string;
  difficulty?: string;
  evidenceRichness?: string;
  timeMode?: string;
}

export interface GenerationJobsListParams {
  status?: string;
  page: number;
  pageSize: number;
}

export const topicKeys = {
  all: ["admin", "topics"] as const,
  lists: () => [...topicKeys.all, "list"] as const,
  list: (params: TopicsListParams) => [...topicKeys.lists(), params] as const,
  tags: () => [...topicKeys.all, "tags"] as const,
  evidenceRichness: (angleTypeIds: string[], difficultyId: string | null) =>
    [...topicKeys.all, "evidenceRichness", angleTypeIds, difficultyId] as const,
  drafts: () => [...topicKeys.all, "drafts"] as const,
  detail: (id: string) => [...topicKeys.all, "detail", id] as const,
  jobs: () => [...topicKeys.all, "jobs"] as const,
  jobList: (params: GenerationJobsListParams) => [...topicKeys.jobs(), params] as const,
};

/** Archetype catalogue (name, question template, A-targets) for topic forms. */
export function useArchetypes() {
  return useQuery<Archetype[]>({
    queryKey: [...topicKeys.all, "archetypes"],
    queryFn: () => api.getArchetypes(),
  });
}

/** Full details for a single topic, gated by `enabled` so it only fetches when an id is set. */
export function useTopic(topicId: string | undefined) {
  return useQuery<Topic>({
    queryKey: topicKeys.detail(topicId ?? ""),
    queryFn: () => api.getTopic(topicId as string),
    enabled: Boolean(topicId),
  });
}

/** Tags grouped by `tag_group` (topic_area, angle_type, …) for filters + forms. */
export function useTopicTags() {
  return useQuery<Record<string, TagResponse[]>>({
    queryKey: topicKeys.tags(),
    queryFn: async () => {
      const tags = await api.getTags();
      const byGroup: Record<string, TagResponse[]> = {};
      for (const tag of tags) {
        if (!byGroup[tag.tag_group]) byGroup[tag.tag_group] = [];
        byGroup[tag.tag_group].push(tag);
      }
      return byGroup;
    },
  });
}

/**
 * Evidence-richness lookup for the chosen angle types + difficulty. Gated by
 * `enabled` so it only runs once an angle type is selected.
 */
export function useEvidenceRichness(angleTypeIds: string[], difficultyId: string | null) {
  return useQuery<{ value: string | null; display_name: string | null }>({
    queryKey: topicKeys.evidenceRichness(angleTypeIds, difficultyId),
    queryFn: () => api.getEvidenceRichness(angleTypeIds, difficultyId),
    enabled: angleTypeIds.length > 0,
  });
}

/** The paginated, filtered, sorted topics list. */
export function useTopics(params: TopicsListParams) {
  return useQuery<Page<Topic>>({
    queryKey: topicKeys.list(params),
    queryFn: () =>
      api.getTopics({
        page: params.page,
        pageSize: params.pageSize,
        topicAreaId: params.topicAreaId,
        status: params.status,
        q: params.q,
        sort_by: params.sort_by,
        sort_order: params.sort_order,
        includeDrafts: params.includeDrafts,
        topicArea: params.topicArea,
        angleType: params.angleType,
        difficulty: params.difficulty,
        evidenceRichness: params.evidenceRichness,
        timeMode: params.timeMode,
      }),
  });
}

/** All draft topics (the review-queue feed). */
export function useDraftTopics() {
  return useQuery<Topic[]>({
    queryKey: topicKeys.drafts(),
    queryFn: () => api.getDraftTopics(),
  });
}

/**
 * Generation-jobs list. Polls every 3s while any job is still running
 * (pending/in_progress), then stops. `enabled` gates the query so it only runs
 * when the generate view is active.
 */
export function useGenerationJobs(
  params: GenerationJobsListParams,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: topicKeys.jobList(params),
    queryFn: () =>
      api.getGenerationJobs({
        status: params.status,
        page: params.page,
        pageSize: params.pageSize,
      }),
    enabled,
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const anyRunning = items.some(
        (j: GenerationJob) => j.status === "pending" || j.status === "in_progress"
      );
      return anyRunning ? 3000 : false;
    },
  });
}

/** Invalidate every Topics query (lists, drafts, jobs) after a write. */
function useInvalidateTopics() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: topicKeys.all });
}

export function useCreateTopic() {
  const invalidate = useInvalidateTopics();
  return useMutation({
    mutationFn: (data: Parameters<typeof api.createTopic>[0]) => api.createTopic(data),
    onSuccess: invalidate,
  });
}

export function useUpdateTopic() {
  const invalidate = useInvalidateTopics();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.updateTopic>[1] }) =>
      api.updateTopic(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteTopic() {
  const invalidate = useInvalidateTopics();
  return useMutation({
    mutationFn: (id: string) => api.deleteTopic(id),
    onSuccess: invalidate,
  });
}

export function useApproveTopic() {
  const invalidate = useInvalidateTopics();
  return useMutation({
    mutationFn: (id: string) => api.approveTopic(id),
    onSuccess: invalidate,
  });
}

export function useRejectTopic() {
  const invalidate = useInvalidateTopics();
  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason: string }) =>
      api.rejectTopic(id, rejectionReason),
    onSuccess: invalidate,
  });
}

export function useBulkApproveTopics() {
  const invalidate = useInvalidateTopics();
  return useMutation({
    mutationFn: (topicIds: string[]) => api.bulkApproveTopics(topicIds),
    onSuccess: invalidate,
  });
}

export function useGenerateTopics() {
  const invalidate = useInvalidateTopics();
  return useMutation<GenerationJob, Error, { eventId: string; count: number }>({
    mutationFn: ({ eventId, count }) => api.generateFromSeedEvent(eventId, count),
    onSuccess: invalidate,
  });
}
